import React, { createContext, useContext, useState, ReactNode } from 'react';
import { User, AuthContextType } from '@/types';

const MockAuthContext = createContext<AuthContextType | undefined>(undefined);

export const useMockAuth = () => {
  const context = useContext(MockAuthContext);
  if (context === undefined) {
    throw new Error('useMockAuth must be used within a MockAuthProvider');
  }
  return context;
};

interface MockAuthProviderProps {
  children: ReactNode;
}

// Mock user data for development
const mockUser: User = {
  id: 'dev-user-1',
  email: 'dev@example.com',
  name: 'Dev User',
  level: 5,
  token: 'mock-bearer-token-for-development',
  usage: {
    burned_tokens: 1500,
    completion: 2000,
    limit: 10000,
    prompt: 3000,
    total: 5000,
  },
};

export const MockAuthProvider: React.FC<MockAuthProviderProps> = ({
  children,
}) => {
  const [user, setUser] = useState<User | null>(mockUser);
  const [isLoading, setIsLoading] = useState(false);

  const login = async (email: string, _password: string) => {
    console.log('🔧 Mock login with email:', email);
    setIsLoading(true);
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 500));
    setUser(mockUser);
    setIsLoading(false);
    console.log('✅ Mock login successful');
  };

  const loginWithUsername = async (username: string, _password: string) => {
    console.log('🔧 Mock login with username:', username);
    setIsLoading(true);
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 500));
    setUser(mockUser);
    setIsLoading(false);
    console.log('✅ Mock login successful');
  };

  const signup = async (email: string, _password: string, name: string) => {
    console.log('🔧 Mock signup:', { email, name });
    setIsLoading(true);
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 500));
    const newUser = { ...mockUser, email, name };
    setUser(newUser);
    setIsLoading(false);
    console.log('✅ Mock signup successful');
  };

  const logout = async () => {
    console.log('🔧 Mock logout');
    setUser(null);
  };

  const requestOTP = async (_email?: string, _username?: string, _action?: string) => {
    console.log('🔧 Mock OTP request');
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 500));
    console.log('✅ Mock OTP sent');
  };

  const verifyOTP = async (
    otp: string,
    action: string,
    _email?: string,
    _username?: string
  ): Promise<string> => {
    console.log('🔧 Mock OTP verification:', { otp, action });
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 500));
    console.log('✅ Mock OTP verified');
    return 'mock-action-token';
  };

  const changePassword = async (
    _oldPassword: string,
    _newPassword: string,
    _confirmPassword: string,
    _actionToken?: string
  ) => {
    console.log('🔧 Mock password change');
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 500));
    console.log('✅ Mock password changed');
  };

  const refreshUser = async () => {
    console.log('🔧 Mock user refresh');
    // User data is already set, no need to refresh in mock mode
  };

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    loginWithUsername,
    logout,
    signup,
    requestOTP,
    verifyOTP,
    changePassword,
    refreshUser,
  };

  return (
    <MockAuthContext.Provider value={value}>
      {children}
    </MockAuthContext.Provider>
  );
};
